import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useUser } from '@/hooks/use-user';
import { UserIdManager } from '@/lib/utils/user-id';
import { Locale } from '@/types/locale';

import { useUserTourState } from './use-user-tour-state';

interface UseUserTourWithUserIdProps {
  dealId: string;
  dealName?: string | null;
  dealState?: string | null;
  editionMode?: string | null;
  page: 'brand' | 'choose';
  brandId?: string | null;
}

export function useUserTourWithUserId({
  dealId,
  dealName,
  dealState,
  editionMode,
  page,
  brandId,
}: UseUserTourWithUserIdProps) {
  const { i18n } = useTranslation();
  const user = useUser();
  const isInternalUser = user?.isInternalUser ?? false;
  const { identify, group, start, isInitialized } = useUserTourState();

  useEffect(() => {
    if (isInternalUser) return;
    if (!isInitialized || !dealId || !dealName || !i18n) return;

    // Récupérer ou générer le userId unique
    const userId = UserIdManager.getUserId();
    const companyId = `brand-company-${brandId || dealId}`;

    // Identifier l'utilisateur avec son userId unique
    identify(userId, {
      role: page,
      brandId: brandId || undefined,
      dealId: dealId,
      dealName: dealName,
      dealState: dealState || undefined,
      editionMode: editionMode || undefined,
      locale: i18n.language === 'en' ? Locale.US : Locale.FR,
      page: page,
    });

    // Associer le groupe (marque)
    group(companyId, {
      name: `${page === 'brand' ? 'Brand' : 'Choose'} ${brandId || dealId}`,
      role: page,
      dealId: dealId,
      dealName: dealName,
    });

    // Démarrer le tour
    start();
  }, [
    i18n,
    isInitialized,
    identify,
    group,
    start,
    dealId,
    dealName,
    dealState,
    editionMode,
    page,
    brandId,
    isInternalUser,
  ]);

  return {
    isInitialized,
  };
}
